import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { renewRental } from '../../Redux/Actions/actions';
import { RENTAL_TYPE, formatCurrency } from '../../Views/RentalManagement/utils/rentalTypeHelpers';
import style from './RenewContractModal.module.css';

/**
 * Modal de renovación de contrato. Cierra el contrato anterior (queda en historial)
 * y crea uno nuevo con genealogía (renewalOf). Soporta tipo regular y admin.
 *
 * Props:
 * - contract: objeto de contrato normalizado (usa contractStart/End, rentValue, etc.) para prefill
 * - propertyTitle?: string
 * - type?: 'regular' | 'admin'
 * - onClose: () => void
 * - onSuccess?: (result) => void  // result.newRental.id para navegar
 */
const ADJUSTMENT_TYPES = ['ICL', 'IPC', 'porcentual', 'manual', 'contractual', 'sin-ajuste'];

const RenewContractModal = ({ contract = {}, propertyTitle, type = RENTAL_TYPE.REGULAR, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.userId);
  const userDetails = useSelector((state) => state.userDetails);
  const renewLoading = useSelector((state) => state.renewLoading);

  const prevRent = contract.currentRentValue ?? contract.rentValue ?? 0;

  const [form, setForm] = useState({
    contractStart: '',
    contractEnd: '',
    rentValue: prevRent || '',
    currency: contract.currency || 'ARS',
    adminFeePercent: contract.adminFeePercent ?? 10,
    paymentDueDay: contract.paymentDueDay ?? 10,
    adjustmentType: contract.adjustmentType || 'ICL',
    adjustmentFrequency: contract.adjustmentFrequency ?? 12,
    adjustmentPercentage: contract.adjustmentPercentage ?? '',
    hasSignedContract: false,
    contractSignedDate: '',
    notes: '',
    copyTenants: true,
    copyAdjustmentConfig: true,
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.contractStart) e.contractStart = 'Requerido';
    if (!form.contractEnd) e.contractEnd = 'Requerido';
    if (form.contractStart && form.contractEnd && form.contractEnd <= form.contractStart) {
      e.contractEnd = 'Debe ser posterior al inicio';
    }
    const fee = Number(form.adminFeePercent);
    if (!Number.isInteger(fee) || fee < 0 || fee > 100) {
      e.adminFeePercent = 'Entero entre 0 y 100';
    }
    return e;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = async () => {
    if (hasErrors) {
      toast.warn('Revisá los campos obligatorios.');
      return;
    }
    const payload = {
      contractStart: form.contractStart,
      contractEnd: form.contractEnd,
      currency: form.currency,
      adminFeePercent: Number(form.adminFeePercent),
      paymentDueDay: Number(form.paymentDueDay) || 10,
      adjustmentType: form.adjustmentType,
      adjustmentFrequency: Number(form.adjustmentFrequency) || 12,
      hasSignedContract: form.hasSignedContract,
      copyTenants: form.copyTenants,
      copyAdjustmentConfig: form.copyAdjustmentConfig,
      performedBy: userId || undefined,
      performedByName: userDetails?.name || userDetails?.fullName || undefined,
    };
    if (form.rentValue !== '' && form.rentValue !== null) payload.rentValue = Number(form.rentValue);
    if (form.adjustmentPercentage !== '' && form.adjustmentPercentage !== null) {
      payload.adjustmentPercentage = Number(form.adjustmentPercentage);
    }
    if (form.hasSignedContract && form.contractSignedDate) payload.contractSignedDate = form.contractSignedDate;
    if (form.notes.trim()) payload.notes = form.notes.trim();

    try {
      setSubmitting(true);
      const result = await dispatch(renewRental(contract.id, payload, type));
      toast.success('Contrato renovado. El anterior se conserva en el historial.');
      if (onSuccess) onSuccess(result);
    } catch (err) {
      const code = err?.response?.data?.errorCode;
      if (code === 'RENTAL_ALREADY_RENEWED') {
        toast.error('Este contrato ya fue renovado.');
      } else if (code === 'PROPERTY_HAS_ACTIVE_RENTAL') {
        toast.error('La propiedad ya tiene otro contrato activo.');
      } else {
        toast.error(
          err?.response?.data?.message || err?.message || 'No se pudo renovar el contrato.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || renewLoading;

  return (
    <div className={style.modal} onClick={busy ? undefined : onClose}>
      <div className={style.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3 className={style.modalTitle}>Renovar contrato</h3>
        <p className={style.subtitle}>
          {propertyTitle ? `${propertyTitle} — ` : ''}El contrato actual se marcará como vencido y se
          creará uno nuevo enlazado (genealogía).
        </p>

        <div className={style.previewBox}>
          <span>Canon anterior: <strong>{formatCurrency(prevRent, contract.currency || 'ARS')}</strong></span>
        </div>

        <div className={style.row}>
          <label className={style.label}>
            Inicio <span className={style.required}>*</span>
            <input
              type="date"
              className={`${style.input} ${errors.contractStart ? style.inputError : ''}`}
              value={form.contractStart}
              onChange={(e) => setField('contractStart', e.target.value)}
              disabled={busy}
            />
            {errors.contractStart && <span className={style.errText}>{errors.contractStart}</span>}
          </label>
          <label className={style.label}>
            Finalización <span className={style.required}>*</span>
            <input
              type="date"
              className={`${style.input} ${errors.contractEnd ? style.inputError : ''}`}
              value={form.contractEnd}
              onChange={(e) => setField('contractEnd', e.target.value)}
              disabled={busy}
            />
            {errors.contractEnd && <span className={style.errText}>{errors.contractEnd}</span>}
          </label>
        </div>

        <div className={style.row}>
          <label className={style.label}>
            Nuevo canon
            <input
              type="number"
              min="0"
              className={style.input}
              value={form.rentValue}
              onChange={(e) => setField('rentValue', e.target.value)}
              disabled={busy}
              placeholder={String(prevRent)}
            />
          </label>
          <label className={style.label}>
            Moneda
            <select
              className={style.input}
              value={form.currency}
              onChange={(e) => setField('currency', e.target.value)}
              disabled={busy}
            >
              <option value="ARS">ARS</option>
              <option value="USD">USD</option>
            </select>
          </label>
        </div>

        <div className={style.row}>
          <label className={style.label}>
            Canon administración (%)
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              className={`${style.input} ${errors.adminFeePercent ? style.inputError : ''}`}
              value={form.adminFeePercent}
              onChange={(e) => setField('adminFeePercent', e.target.value)}
              disabled={busy}
            />
            {errors.adminFeePercent && <span className={style.errText}>{errors.adminFeePercent}</span>}
          </label>
          <label className={style.label}>
            Día de pago
            <input
              type="number"
              min="1"
              max="31"
              className={style.input}
              value={form.paymentDueDay}
              onChange={(e) => setField('paymentDueDay', e.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        <div className={style.row}>
          <label className={style.label}>
            Tipo de ajuste
            <select
              className={style.input}
              value={form.adjustmentType}
              onChange={(e) => setField('adjustmentType', e.target.value)}
              disabled={busy}
            >
              {ADJUSTMENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label className={style.label}>
            Frecuencia (meses)
            <input
              type="number"
              min="1"
              className={style.input}
              value={form.adjustmentFrequency}
              onChange={(e) => setField('adjustmentFrequency', e.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        <label className={style.checkboxRow}>
          <input
            type="checkbox"
            checked={form.hasSignedContract}
            onChange={(e) => setField('hasSignedContract', e.target.checked)}
            disabled={busy}
          />
          <span>Contrato firmado</span>
        </label>
        {form.hasSignedContract && (
          <label className={style.label}>
            Fecha de firma
            <input
              type="date"
              className={style.input}
              value={form.contractSignedDate}
              onChange={(e) => setField('contractSignedDate', e.target.value)}
              disabled={busy}
            />
          </label>
        )}

        <label className={style.checkboxRow}>
          <input
            type="checkbox"
            checked={form.copyTenants}
            onChange={(e) => setField('copyTenants', e.target.checked)}
            disabled={busy}
          />
          <span>Copiar inquilinos del contrato anterior</span>
        </label>
        <label className={style.checkboxRow}>
          <input
            type="checkbox"
            checked={form.copyAdjustmentConfig}
            onChange={(e) => setField('copyAdjustmentConfig', e.target.checked)}
            disabled={busy}
          />
          <span>Copiar configuración de ajustes</span>
        </label>

        <label className={style.label}>
          Notas
          <textarea
            className={style.textarea}
            rows={2}
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            disabled={busy}
          />
        </label>

        <div className={style.actions}>
          <button type="button" className={style.cancelBtn} onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className={style.confirmBtn}
            onClick={handleSubmit}
            disabled={busy || hasErrors}
          >
            {busy ? 'Renovando...' : 'Renovar contrato'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenewContractModal;
